/**
 * SeoHead — per-album document title, description, OG, and Twitter meta.
 *
 * On the client this is a useEffect that mutates <head> tags (title +
 * meta tags identified by `data-nyx-seo`). On the server (during prerender)
 * we don't render any DOM here — the prerender script imports
 * `buildSeoTags()` directly and injects the resulting strings into
 * `dist/<id>.html`.
 *
 * Why not react-helmet-async?  We hit ESM/CJS dual-package issues during
 * SSR, and the surface we need is small enough to keep in-house.
 */

import { useEffect } from 'react';
import type { AvailableAlbumModule } from '@/types/album';

const SITE_NAME = 'Nyx';
const DEFAULT_ORIGIN = 'https://nyx.space';
const TAG_FLAG = 'data-nyx-seo';

export interface SeoTagSpec {
  title: string;
  description: string;
  canonical: string;
  ogImage: string;
  themeColor: string;
  origin: string;
}

function buildDescription(album: AvailableAlbumModule): string {
  const { library, deepDive } = album;
  return `${library.album} de ${library.artist} (${deepDive.year}), leído como ${deepDive.arch_type.toLowerCase()}. Estructura, significado y notas para escuchar en vivo — en Nyx.`;
}

export function buildSeoSpec(
  album: AvailableAlbumModule,
  origin: string = DEFAULT_ORIGIN
): SeoTagSpec {
  const { library } = album;
  return {
    title: `${library.album} · ${library.artist} — Nyx`,
    description: buildDescription(album),
    canonical: `${origin}/?album=${library.id}`,
    ogImage: `${origin}/og/${library.id}.png`,
    themeColor: library.palette.bg,
    origin,
  };
}

/**
 * Render the head tags as raw HTML strings, suitable for injection during
 * the prerender step. Each tag carries `data-nyx-seo` so client-side updates
 * can locate and replace them on swap.
 */
export function renderSeoTagsHTML(spec: SeoTagSpec): string {
  const esc = (v: string): string =>
    v
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  const tag = (attrs: Record<string, string>): string => {
    const pairs = Object.entries(attrs)
      .map(([k, v]) => `${k}="${esc(v)}"`)
      .join(' ');
    return `<meta ${TAG_FLAG} ${pairs} />`;
  };
  return [
    `<title ${TAG_FLAG}>${esc(spec.title)}</title>`,
    tag({ name: 'description', content: spec.description }),
    `<link ${TAG_FLAG} rel="canonical" href="${esc(spec.canonical)}" />`,
    tag({ property: 'og:type', content: 'website' }),
    tag({ property: 'og:site_name', content: SITE_NAME }),
    tag({ property: 'og:url', content: spec.canonical }),
    tag({ property: 'og:title', content: spec.title }),
    tag({ property: 'og:description', content: spec.description }),
    tag({ property: 'og:image', content: spec.ogImage }),
    tag({ property: 'og:image:width', content: '1200' }),
    tag({ property: 'og:image:height', content: '630' }),
    tag({ property: 'og:locale', content: 'es_AR' }),
    tag({ name: 'twitter:card', content: 'summary_large_image' }),
    tag({ name: 'twitter:title', content: spec.title }),
    tag({ name: 'twitter:description', content: spec.description }),
    tag({ name: 'twitter:image', content: spec.ogImage }),
    tag({ name: 'theme-color', content: spec.themeColor }),
  ].join('\n    ');
}

/**
 * Client-side mutator. Replaces all elements bearing `data-nyx-seo` with
 * the new set, and updates `document.title` + `<html lang>` + `data-album`.
 */
function applySeoToDom(album: AvailableAlbumModule): void {
  if (typeof document === 'undefined') return;
  const spec = buildSeoSpec(album);
  document.title = spec.title;
  const html = document.documentElement;
  html.setAttribute('lang', 'es');
  html.setAttribute('data-album', album.library.id);

  // Sweep existing SEO tags so we don't duplicate.
  document.querySelectorAll(`[${TAG_FLAG}]`).forEach((el) => {
    if (el.tagName !== 'TITLE') el.remove();
  });

  const head = document.head;
  const make = (attrs: Record<string, string>): HTMLElement => {
    const m = document.createElement('meta');
    Object.entries(attrs).forEach(([k, v]) => m.setAttribute(k, v));
    m.setAttribute(TAG_FLAG, '');
    return m;
  };
  const link = (rel: string, href: string): HTMLElement => {
    const l = document.createElement('link');
    l.setAttribute('rel', rel);
    l.setAttribute('href', href);
    l.setAttribute(TAG_FLAG, '');
    return l;
  };

  const updates: HTMLElement[] = [
    make({ name: 'description', content: spec.description }),
    link('canonical', spec.canonical),
    make({ property: 'og:type', content: 'website' }),
    make({ property: 'og:site_name', content: SITE_NAME }),
    make({ property: 'og:url', content: spec.canonical }),
    make({ property: 'og:title', content: spec.title }),
    make({ property: 'og:description', content: spec.description }),
    make({ property: 'og:image', content: spec.ogImage }),
    make({ property: 'og:image:width', content: '1200' }),
    make({ property: 'og:image:height', content: '630' }),
    make({ property: 'og:locale', content: 'es_AR' }),
    make({ name: 'twitter:card', content: 'summary_large_image' }),
    make({ name: 'twitter:title', content: spec.title }),
    make({ name: 'twitter:description', content: spec.description }),
    make({ name: 'twitter:image', content: spec.ogImage }),
    make({ name: 'theme-color', content: spec.themeColor }),
  ];
  for (const el of updates) head.appendChild(el);
}

interface SeoHeadProps {
  album: AvailableAlbumModule;
}

export function SeoHead({ album }: SeoHeadProps): null {
  useEffect(() => {
    applySeoToDom(album);
  }, [album]);
  return null;
}
